// src/components/SearchForm.js
import React, { useEffect, useState, forwardRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from './ui';

const SearchForm = forwardRef(({ 
  searchTerm, 
  setSearchTerm, 
  handleSearch, 
  isLoading = false,
  entityType = 'person',
  setEntityType,
  usageStats = null
}, ref) => {
  // Get translation function
  const t = useTranslation();
  
  // Check if search is disabled due to limits
  const isAtLimit = usageStats && !usageStats.allowed;
  const isDisabled = isLoading || isAtLimit;
  
  // Handle entity type change
  const handleEntityTypeChange = (e) => {
    if (!isDisabled) {
      setEntityType(e.target.value);
    }
  };
  
  return (
    <form 
      className={`mb-2 relative z-10 transition-opacity duration-300 ${isDisabled ? 'opacity-80' : ''}`}
      ref={ref}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isDisabled) handleSearch();
      }}
    >
      <div className="relative w-full">
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none z-1">
          <i className="fa fa-search"></i>
        </span>
        <input
          type="text"
          placeholder={isAtLimit ? 'Search limit reached - upgrade to continue' : t('search.placeholder')}
          value={searchTerm}
          onChange={(e) => !isDisabled && setSearchTerm(e.target.value)}
          className={`w-full py-3.5 pl-10 pr-[180px] sm:pr-[220px] text-base rounded-lg border
            bg-gray-50 dark:bg-gray-700 
            text-gray-900 dark:text-gray-100 
            border-gray-300 dark:border-gray-700
            transition-all duration-200 
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
            dark:focus:ring-blue-600 dark:focus:border-blue-600
            placeholder:sm:opacity-100 placeholder:opacity-0
            ${isDisabled ? 'bg-gray-100 dark:bg-gray-900/70 text-gray-500 dark:text-gray-400 cursor-not-allowed' : ''}`}
          readOnly={isDisabled}
          disabled={isAtLimit}
        />
        
        {/* Entity Type Selector inside search bar */}
        <div className="absolute right-[90px] sm:right-[120px] top-1/2 transform -translate-y-1/2 h-8">
          <div className="bg-gray-100 dark:bg-gray-600 rounded-md flex items-center h-full shadow-sm border border-gray-200 dark:border-gray-500">
            <button
              type="button"
              className={`h-full px-2 sm:px-3 rounded-l-md text-xs sm:text-sm font-medium transition-all duration-200 flex items-center ${
                entityType === 'person'
                  ? 'bg-blue-600 dark:bg-blue-700 text-white dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
              } ${isDisabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={(e) => {
                e.preventDefault();
                if (!isDisabled && entityType !== 'person') handleEntityTypeChange({ target: { value: 'person' } });
              }}
              disabled={isDisabled}
              title={t('search.personType') || 'People'}
            >
              <i className={`fa-solid fa-user text-sm ${entityType === 'person' ? 'text-white' : ''}`}></i>
              <span className="ml-1 hidden sm:inline">{t('search.personType') || 'People'}</span>
              <input
                type="radio"
                name="entityType"
                value="person"
                checked={entityType === 'person'}
                onChange={handleEntityTypeChange}
                className="sr-only"
                disabled={isDisabled}
              />
            </button>
            <button
              type="button"
              className={`h-full px-2 sm:px-3 rounded-r-md text-xs sm:text-sm font-medium transition-all duration-200 flex items-center ${
                entityType === 'enterprise'
                  ? 'bg-blue-600 dark:bg-blue-700 text-white dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
              } ${isDisabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={(e) => {
                e.preventDefault();
                if (!isDisabled && entityType !== 'enterprise') handleEntityTypeChange({ target: { value: 'enterprise' } });
              }}
              disabled={isDisabled}
              title={t('search.enterpriseType') || 'Companies'}
            >
              <i className={`fa-solid fa-building text-sm ${entityType === 'enterprise' ? 'text-white' : ''}`}></i>
              <span className="ml-1 hidden sm:inline">{t('search.enterpriseType') || 'Companies'}</span>
              <input
                type="radio"
                name="entityType"
                value="enterprise"
                checked={entityType === 'enterprise'}
                onChange={handleEntityTypeChange}
                className="sr-only"
                disabled={isDisabled}
              />
            </button>
          </div>
        </div>
        
        <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2">
          <Button
            type="submit"
            variant="primary"
            size="md"
            isLoading={isLoading}
            disabled={isAtLimit}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                return false;
              }
            }}
          >
            {t('search.button')}
          </Button>
        </div>
      </div>
    </form>
  );
});

export default SearchForm;
